// React Query hooks for the Wave E users (staff) module.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createStaff,
  deleteStaff,
  getStaff,
  listStaff,
  triggerPasswordReset,
  updateStaff,
  type ListStaffFilters,
} from '../../../../api/users';
import { queryKeys } from '../../../../api/queryKeys';
import { useToast } from '../../../../hooks/useToast';
import type {
  CreateStaffInput,
  StaffListResponse,
  StaffSummary,
  UpdateStaffInput,
} from '../../../../types/api';

export function useStaffList(filters: ListStaffFilters) {
  return useQuery<StaffListResponse>({
    queryKey: queryKeys.users.list(filters as unknown as Record<string, unknown>),
    queryFn: () => listStaff(filters),
    staleTime: 30_000,
  });
}

export function useStaffDetail(uuid: string | undefined) {
  return useQuery<StaffSummary>({
    queryKey: uuid ? queryKeys.users.detail(uuid) : ['users', 'detail', 'none'],
    queryFn: () => getStaff(uuid as string),
    enabled: Boolean(uuid),
    staleTime: 30_000,
  });
}

export function useCreateStaff() {
  const qc = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: (input: CreateStaffInput) => createStaff(input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.users.all });
      toast.success('User invited.');
    },
    onError: (err: unknown) => {
      const message =
        err instanceof Error && err.message ? err.message : 'Failed to invite user.';
      toast.error(message);
    },
  });
}

export function useUpdateStaff(uuid: string) {
  const qc = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: (input: UpdateStaffInput) => updateStaff(uuid, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.users.detail(uuid) });
      void qc.invalidateQueries({ queryKey: queryKeys.users.all });
      toast.success('User updated.');
    },
    onError: () => {
      toast.error('Failed to update user.');
    },
  });
}

export function useDeleteStaff() {
  const qc = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: (uuid: string) => deleteStaff(uuid),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.users.all });
      toast.success('User deactivated.');
    },
    onError: () => {
      toast.error('Failed to deactivate user.');
    },
  });
}

export function useTriggerPasswordReset() {
  const toast = useToast();
  return useMutation({
    mutationFn: (uuid: string) => triggerPasswordReset(uuid),
    onSuccess: () => {
      toast.success('Password reset email sent.');
    },
    onError: () => {
      toast.error('Failed to send password reset.');
    },
  });
}
